/**
 * QBridge EA Assistant API Service
 * Handles communication with QBridge local server (localhost:5556) and Backend API
 *
 * QBridge Local Endpoints (localhost:5556):
 * - GET  /api/ea/list                          - List all MQL5 files
 * - GET  /api/ea/content?name=X&includeIncludes=true - Get source code + includes (Auth required)
 * - POST /api/ea/save                          - Save modified EA as _QAI version (Auth required)
 * - GET  /api/status                           - Check QBridge status
 *
 * Backend API Endpoints (api.qforgex.com):
 * - POST /api/q/ea/analyze - Analyze EA via Q.AI
 * - POST /api/q/ea/modify  - Request modifications
 * - POST /api/q/ea/cost    - Get token cost estimate
 * - GET  /api/q/ea/history - User's analysis history
 *
 * NOTE: For QBridge status checking, prefer importing from qbridge.service.js
 * which has comprehensive status checking with MT5 detection fallback.
 */

import { baseURL } from '../config';

// QBridge local server URL
const QBRIDGE_URL = 'http://localhost:5556';

// ═══════════════════════════════════════════════════════════════════════════
// CONSTANTS
// ═══════════════════════════════════════════════════════════════════════════

/**
 * File type configurations for display
 */
export const EA_FILE_TYPES = {
  expert: { label: 'EA', color: '#3B82F6', fullName: 'Expert Advisor' },
  indicator: { label: 'IND', color: '#22C55E', fullName: 'Indicator' },
  script: { label: 'SCR', color: '#F97316', fullName: 'Script' }
};

/**
 * Default token costs for EA operations (estimates)
 */
export const EA_ANALYSIS_COST = 5;   // Analyze operation
export const EA_MODIFY_COST = 10;    // Modify operation
export const EA_ANALYSIS_DEFAULT_COST = 10; // Legacy fallback

/**
 * File size limit for EA uploads (500KB)
 */
export const EA_MAX_FILE_SIZE = 500 * 1024; // 500KB in bytes

/**
 * API timeout for EA operations (60 seconds)
 */
export const EA_API_TIMEOUT = 60000; // 60 seconds in ms

// ═══════════════════════════════════════════════════════════════════════════
// QBRIDGE LOCAL SERVER API FUNCTIONS
// ═══════════════════════════════════════════════════════════════════════════

/**
 * Check QBridge server status (EA Assistant specific)
 *
 * NOTE: This is a simplified version for EA Assistant.
 * For comprehensive QBridge + MT5 detection with fallback,
 * use checkQBridgeStatus from '../services/qbridge.service'.
 *
 * @returns {Promise<{online: boolean, version: string|null, status: string}>}
 */
export const checkQBridgeStatusEA = async () => {
  try {
    const response = await fetch(`${QBRIDGE_URL}/api/status`, {
      method: 'GET',
      headers: { 'Content-Type': 'application/json' }
    });

    if (!response.ok) {
      return { online: false, version: null, status: 'offline' };
    }

    const data = await response.json();
    return {
      online: data.online ?? (data.status === 'ready'),
      version: data.version || '1.0.0',
      status: data.status || 'ready'
    };
  } catch (error) {
    console.warn('[qbridgeEaApi] QBridge not reachable:', error.message);
    return { online: false, version: null, status: 'offline' };
  }
};

// Legacy alias for backwards compatibility
export const checkQBridgeStatus = checkQBridgeStatusEA;

/**
 * Get list of all MQL5 files from user's MT5 installation
 * Endpoint: GET /api/ea/list
 *
 * @param {object} options - Query options
 * @param {string} options.type - Filter by type: 'expert' | 'indicator' | 'script' | 'all'
 * @param {boolean} options.refresh - Force refresh cache
 * @returns {Promise<{
 *   success: boolean,
 *   files: Array<{
 *     name: string,
 *     file_type: string,
 *     size_kb: number,
 *     line_count: number,
 *     content_hash: string,
 *     has_qai_version: boolean
 *   }>,
 *   summary: {
 *     experts: number,
 *     indicators: number,
 *     scripts: number,
 *     total: number
 *   },
 *   error: string|null
 * }>}
 */
export const getEAList = async (options = {}) => {
  const { type = 'all', refresh = false } = options;

  try {
    const params = new URLSearchParams();
    if (type !== 'all') params.append('type', type);
    if (refresh) params.append('refresh', 'true');

    const url = `${QBRIDGE_URL}/api/ea/list${params.toString() ? `?${params}` : ''}`;
    const response = await fetch(url, {
      method: 'GET',
      headers: { 'Content-Type': 'application/json' }
    });

    if (!response.ok) {
      if (response.status === 404) {
        return {
          success: false,
          files: [],
          summary: { experts: 0, indicators: 0, scripts: 0, total: 0 },
          error: 'MT5 not detected. Please ensure MetaTrader 5 is installed.'
        };
      }
      throw new Error(`Failed to get EA list: ${response.status}`);
    }

    const data = await response.json();
    const summary = data.summary || {};

    return {
      success: true,
      files: data.files || [],
      summary: {
        // Support both new (experts) and legacy (experts_count) field names
        experts: summary.experts ?? summary.experts_count ?? 0,
        indicators: summary.indicators ?? summary.indicators_count ?? 0,
        scripts: summary.scripts ?? summary.scripts_count ?? 0,
        total: summary.total ?? (data.files?.length || 0)
      },
      error: null
    };
  } catch (error) {
    console.error('[qbridgeEaApi] getEAList failed:', error);
    return {
      success: false,
      files: [],
      summary: { experts: 0, indicators: 0, scripts: 0, total: 0 },
      error: error.message || 'Failed to connect to QBridge'
    };
  }
};

/**
 * Get EA source code and includes
 * Endpoint: GET /api/ea/content?name=X&includeIncludes=true
 *
 * @param {string} token - JWT token for authentication
 * @param {string} fileName - Name of the EA file
 * @param {boolean} includeIncludes - Include .mqh dependency files
 * @returns {Promise<{
 *   success: boolean,
 *   main_source: string,
 *   includes: Object<string, string>,
 *   line_count: number,
 *   content_hash: string,
 *   error: string|null,
 *   errorCode: number|null
 * }>}
 */
export const getEAContent = async (token, fileName, includeIncludes = true) => {
  try {
    const params = new URLSearchParams({
      name: fileName,
      includeIncludes: includeIncludes.toString()
    });

    const url = `${QBRIDGE_URL}/api/ea/content?${params}`;
    console.log('[qbridgeEaApi] getEAContent request:', {
      url,
      fileName,
      encodedName: encodeURIComponent(fileName)
    });

    const response = await fetch(url, {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      }
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));

      if (response.status === 401) {
        return {
          success: false,
          main_source: '',
          includes: {},
          line_count: 0,
          content_hash: '',
          error: 'Authentication required. Please sign in.',
          errorCode: 401
        };
      }

      if (response.status === 404) {
        console.error('[qbridgeEaApi] getEAContent 404 error:', {
          fileName,
          status: response.status,
          errorData
        });
        return {
          success: false,
          main_source: '',
          includes: {},
          line_count: 0,
          content_hash: '',
          error: `File not found: ${fileName}`,
          errorCode: 404
        };
      }

      if (response.status === 403) {
        return {
          success: false,
          main_source: '',
          includes: {},
          line_count: 0,
          content_hash: '',
          error: 'Access denied. Security validation failed.',
          errorCode: 403
        };
      }

      throw new Error(errorData.message || `Failed to get content: ${response.status}`);
    }

    const data = await response.json();

    // Debug log raw QBridge response
    console.log('[qbridgeEaApi] getEAContent raw response:', {
      hasMainSource: !!data.main_source,
      mainSourceLength: data.main_source?.length || 0,
      mainSourcePreview: data.main_source?.substring(0, 100) || 'EMPTY',
      includesKeys: data.includes ? Object.keys(data.includes) : [],
      lineCount: data.line_count
    });

    return {
      success: true,
      main_source: data.main_source || '',
      includes: data.includes || {},
      line_count: data.line_count || 0,
      content_hash: data.content_hash || '',
      error: null,
      errorCode: null
    };
  } catch (error) {
    console.error('[qbridgeEaApi] getEAContent failed:', error);
    return {
      success: false,
      main_source: '',
      includes: {},
      line_count: 0,
      content_hash: '',
      error: error.message || 'Failed to get EA content',
      errorCode: null
    };
  }
};

/**
 * Save modified EA as _QAI version
 * Endpoint: POST /api/ea/save (Auth Required)
 * CRITICAL: Original file is NEVER modified. Creates new file with _QAI suffix.
 *
 * @param {string} token - JWT token for authentication
 * @param {object} saveData - Save parameters
 * @param {string} saveData.originalName - Original file name (camelCase)
 * @param {string} saveData.modifiedSource - Modified source code (camelCase)
 * @param {Object<string, string>} saveData.modifiedIncludes - Modified include files (camelCase)
 * @returns {Promise<{
 *   success: boolean,
 *   savedPath: string,
 *   fileName: string,
 *   error: string|null,
 *   errorCode: number|null
 * }>}
 */
export const saveEAModified = async (token, saveData) => {
  try {
    // Build request with camelCase keys (QBridge now accepts both)
    const payload = {
      originalName: saveData.originalName || saveData.original_name,
      modifiedSource: saveData.modifiedSource || saveData.modified_source,
      modifiedIncludes: saveData.modifiedIncludes || saveData.modified_includes || {}
    };

    const response = await fetch(`${QBRIDGE_URL}/api/ea/save`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(payload)
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));

      if (response.status === 401) {
        return {
          success: false,
          savedPath: '',
          fileName: '',
          error: 'Authentication required. Please sign in.',
          errorCode: 401
        };
      }

      if (response.status === 409) {
        return {
          success: false,
          savedPath: '',
          fileName: '',
          error: 'File was modified externally. Please refresh and try again.',
          errorCode: 409
        };
      }

      if (response.status === 403) {
        return {
          success: false,
          savedPath: '',
          fileName: '',
          error: 'Security validation failed. Invalid file path.',
          errorCode: 403
        };
      }

      throw new Error(errorData.message || `Save failed: ${response.status}`);
    }

    const data = await response.json();
    return {
      success: true,
      // Support both new (savedPath, fileName) and legacy (path, saved_as) response fields
      savedPath: data.savedPath || data.path || '',
      fileName: data.fileName || data.saved_as || '',
      error: null,
      errorCode: null
    };
  } catch (error) {
    console.error('[qbridgeEaApi] saveEAModified failed:', error);
    return {
      success: false,
      savedPath: '',
      fileName: '',
      error: error.message || 'Failed to save modified EA',
      errorCode: null
    };
  }
};

// ═══════════════════════════════════════════════════════════════════════════
// BACKEND API FUNCTIONS (Q.AI Analysis)
// ═══════════════════════════════════════════════════════════════════════════

/**
 * Get token cost estimate for EA operation
 * Endpoint: POST /api/q/ea/cost
 *
 * @param {string} token - JWT token
 * @param {object} costData - Cost calculation parameters
 * @param {number} costData.lineCount - Number of lines in the EA file
 * @param {Array} costData.modifications - Optional modifications array for modify cost
 * @returns {Promise<{
 *   success: boolean,
 *   estimatedCost: number,
 *   currentBalance: number,
 *   hasSufficientBalance: boolean,
 *   shortfall: number,
 *   purchaseUrl: string|null,
 *   error: string|null
 * }>}
 */
export const getEACost = async (token, costData = {}) => {
  const { lineCount = 0, modifications = [] } = costData;
  const isModify = modifications && modifications.length > 0;
  const defaultCost = isModify ? EA_MODIFY_COST : EA_ANALYSIS_COST;

  try {
    const response = await fetch(`${baseURL}/api/q/ea/cost`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({
        lineCount,
        modifications
      })
    });

    if (!response.ok) {
      if (response.status === 401) {
        return {
          success: false,
          estimatedCost: defaultCost,
          currentBalance: 0,
          hasSufficientBalance: false,
          shortfall: defaultCost,
          purchaseUrl: null,
          error: 'Authentication required'
        };
      }
      throw new Error(`Cost estimate failed: ${response.status}`);
    }

    const data = await response.json();
    const result = data.data || data;

    return {
      success: true,
      estimatedCost: result.estimatedCost || result.estimated_tokens || defaultCost,
      currentBalance: result.currentBalance || result.balance || 0,
      hasSufficientBalance: result.hasSufficientBalance ?? result.sufficient ?? true,
      shortfall: result.shortfall || 0,
      purchaseUrl: result.purchaseUrl || result.purchase_url || null,
      error: null
    };
  } catch (error) {
    console.warn('[qbridgeEaApi] getEACost failed, using default:', error);
    // Return default cost estimate if API fails
    return {
      success: true,
      estimatedCost: defaultCost,
      currentBalance: null, // Unknown
      hasSufficientBalance: true, // Assume sufficient, let analysis handle 402
      shortfall: 0,
      purchaseUrl: null,
      error: null
    };
  }
};

/**
 * Analyze EA with Q.AI
 * Endpoint: POST /api/q/ea/analyze
 *
 * @param {string} token - JWT token
 * @param {object} analysisData - Analysis parameters
 * @param {string} analysisData.name - Name of the EA file
 * @param {string} analysisData.mainSource - Main source code (plain text UTF-8)
 * @param {boolean} analysisData.consentGiven - User consent for analysis (REQUIRED)
 * @param {Object<string, string>} analysisData.includeFiles - Optional include files content
 * @returns {Promise<{
 *   success: boolean,
 *   analysis: {
 *     summary: string,
 *     bugs: Array<{line: number, severity: string, description: string, fix: string}>,
 *     enhancements: Array<{type: string, description: string, priority: string}>,
 *     riskScore: number,
 *     codeQuality: string
 *   },
 *   tokensUsed: number,
 *   balanceRemaining: number,
 *   error: string|null,
 *   errorCode: number|null,
 *   insufficientBalance: boolean,
 *   required: number,
 *   current: number,
 *   purchaseUrl: string|null
 * }>}
 */
export const analyzeEA = async (token, analysisData) => {
  // Create AbortController for timeout
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), EA_API_TIMEOUT);

  try {
    // Validate consent is provided
    if (!analysisData.consentGiven) {
      return {
        success: false,
        analysis: null,
        tokensUsed: 0,
        balanceRemaining: 0,
        error: 'User consent is required to analyze EA code',
        errorCode: 400
      };
    }

    // Get mainSource - support both camelCase and snake_case input
    const mainSource = analysisData.mainSource || analysisData.main_source || analysisData.source || '';

    // Validate mainSource is not empty
    if (!mainSource || mainSource.trim().length === 0) {
      console.error('[qbridgeEaApi] analyzeEA: mainSource is empty or missing');
      return {
        success: false,
        analysis: null,
        tokensUsed: 0,
        balanceRemaining: 0,
        error: 'EA source code is empty or missing. Please reload the file.',
        errorCode: 400
      };
    }

    // Build request payload matching Backend API spec (FIX Engine expects camelCase)
    const payload = {
      name: analysisData.name || analysisData.fileName,
      mainSource: mainSource,
      consentGiven: analysisData.consentGiven
    };

    // Include optional include files if provided (support both naming conventions)
    const includeFiles = analysisData.includeFiles || analysisData.includes || analysisData.include_files;
    if (includeFiles && Object.keys(includeFiles).length > 0) {
      payload.includeFiles = includeFiles;
    }

    // Debug log the payload structure
    console.log('[qbridgeEaApi] analyzeEA payload:', {
      name: payload.name,
      mainSourceLength: payload.mainSource?.length || 0,
      mainSourceType: typeof payload.mainSource,
      hasMainSource: 'mainSource' in payload,
      consentGiven: payload.consentGiven,
      includeFilesCount: payload.includeFiles ? Object.keys(payload.includeFiles).length : 0,
      payloadKeys: Object.keys(payload)
    });

    // Log actual JSON being sent (first 500 chars for debugging)
    const jsonBody = JSON.stringify(payload);
    console.log('[qbridgeEaApi] Actual JSON body (first 500 chars):', jsonBody.substring(0, 500));

    const response = await fetch(`${baseURL}/api/q/ea/analyze`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      },
      body: jsonBody,
      signal: controller.signal
    });

    clearTimeout(timeoutId);

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));

      // Debug log the error response
      console.error('[qbridgeEaApi] analyzeEA error response:', {
        status: response.status,
        statusText: response.statusText,
        errorData: errorData
      });

      if (response.status === 400) {
        // Extract detailed error message from various possible response formats
        const errorMsg = errorData.message
          || errorData.error
          || errorData.errors?.join(', ')
          || errorData.title
          || (typeof errorData === 'string' ? errorData : null)
          || 'Bad Request - check console for details';

        console.error('[qbridgeEaApi] 400 Bad Request details:', {
          fullError: JSON.stringify(errorData, null, 2)
        });

        return {
          success: false,
          analysis: null,
          tokensUsed: 0,
          balanceRemaining: 0,
          error: `Analysis failed: ${errorMsg}`,
          errorCode: 400
        };
      }

      if (response.status === 401) {
        return {
          success: false,
          analysis: null,
          tokensUsed: 0,
          balanceRemaining: 0,
          error: 'Session expired. Please log in again.',
          errorCode: 401
        };
      }

      if (response.status === 402) {
        return {
          success: false,
          analysis: null,
          tokensUsed: 0,
          balanceRemaining: errorData.available || errorData.current || 0,
          error: errorData.error || 'insufficient_balance',
          errorCode: 402,
          insufficientBalance: true,
          required: errorData.required || EA_ANALYSIS_COST,
          available: errorData.available || errorData.current || 0,
          purchaseUrl: errorData.purchaseUrl || errorData.purchase_url || null
        };
      }

      if (response.status === 413) {
        return {
          success: false,
          analysis: null,
          tokensUsed: 0,
          balanceRemaining: 0,
          error: 'File too large. Maximum size is 500KB.',
          errorCode: 413
        };
      }

      throw new Error(errorData.message || `Analysis failed: ${response.status}`);
    }

    const data = await response.json();
    const result = data.data || data;

    // Map response to normalized structure (supports both old and new format)
    return {
      success: true,
      analysis: {
        summary: result.summary || result.analysis?.summary || '',
        bugs: result.bugs || result.analysis?.bugs || result.analysis?.issues || [],
        enhancements: result.enhancements || result.analysis?.enhancements || result.analysis?.suggestions || [],
        riskScore: result.riskScore || result.analysis?.riskScore || 0,
        riskLevel: result.riskLevel || result.analysis?.riskLevel || 'unknown',
        codeQuality: result.codeQuality || result.analysis?.codeQuality || 'unknown'
      },
      tokensUsed: result.tokensUsed || result.tokens_used || 0,
      balanceRemaining: result.balanceRemaining || result.balance_remaining || 0,
      error: null,
      errorCode: null
    };
  } catch (error) {
    clearTimeout(timeoutId);

    // Handle timeout
    if (error.name === 'AbortError') {
      return {
        success: false,
        analysis: null,
        tokensUsed: 0,
        balanceRemaining: 0,
        error: 'Analysis timed out. Please try again.',
        errorCode: 408
      };
    }

    console.error('[qbridgeEaApi] analyzeEA failed:', error);
    return {
      success: false,
      analysis: null,
      tokensUsed: 0,
      balanceRemaining: 0,
      error: error.message || 'EA analysis failed',
      errorCode: null
    };
  }
};

/**
 * Request EA modifications from Q.AI
 * Endpoint: POST /api/q/ea/modify
 *
 * @param {string} token - JWT token
 * @param {object} modifyData - Modification parameters
 * @param {string} modifyData.name - Name of the EA file
 * @param {string} modifyData.mainSource - Current source code (plain text UTF-8)
 * @param {Array<{type: string, bugs?: number[], feature?: string}>} modifyData.modifications - Modifications to apply
 * @param {boolean} modifyData.consentGiven - User consent (REQUIRED)
 * @returns {Promise<{
 *   success: boolean,
 *   modifiedSource: string,
 *   modifiedIncludes: Object<string, string>,
 *   changes: Array<{line: number, type: string, description: string}>,
 *   tokensUsed: number,
 *   balanceRemaining: number,
 *   error: string|null,
 *   errorCode: number|null,
 *   insufficientBalance: boolean,
 *   required: number,
 *   current: number,
 *   purchaseUrl: string|null
 * }>}
 */
export const modifyEA = async (token, modifyData) => {
  // Create AbortController for timeout
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), EA_API_TIMEOUT);

  try {
    // Validate consent is provided
    if (!modifyData.consentGiven) {
      return {
        success: false,
        modifiedSource: '',
        modifiedIncludes: {},
        changes: [],
        tokensUsed: 0,
        balanceRemaining: 0,
        error: 'User consent is required to modify EA code',
        errorCode: 400
      };
    }

    // Get mainSource - support both camelCase and snake_case input
    const mainSource = modifyData.mainSource || modifyData.main_source || modifyData.source || '';

    // Validate mainSource is not empty
    if (!mainSource || mainSource.trim().length === 0) {
      console.error('[qbridgeEaApi] modifyEA: mainSource is empty or missing');
      return {
        success: false,
        modifiedSource: '',
        modifiedIncludes: {},
        changes: [],
        tokensUsed: 0,
        balanceRemaining: 0,
        error: 'EA source code is empty or missing. Please reload the file.',
        errorCode: 400
      };
    }

    // Build request payload matching Backend API spec (FIX Engine expects camelCase)
    const payload = {
      name: modifyData.name || modifyData.fileName,
      mainSource: mainSource,
      modifications: modifyData.modifications || [],
      consentGiven: modifyData.consentGiven
    };

    // Support legacy instruction-based modifications
    if (modifyData.instruction && !modifyData.modifications?.length) {
      payload.modifications = [{
        type: 'custom',
        instruction: modifyData.instruction
      }];
    }

    // Debug log the payload structure (remove in production)
    console.log('[qbridgeEaApi] modifyEA payload:', {
      name: payload.name,
      mainSourceLength: payload.mainSource?.length || 0,
      modificationsCount: payload.modifications?.length || 0,
      consentGiven: payload.consentGiven
    });

    const response = await fetch(`${baseURL}/api/q/ea/modify`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(payload),
      signal: controller.signal
    });

    clearTimeout(timeoutId);

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));

      if (response.status === 400) {
        return {
          success: false,
          modifiedSource: '',
          modifiedIncludes: {},
          changes: [],
          tokensUsed: 0,
          balanceRemaining: 0,
          error: errorData.message || 'User consent is required',
          errorCode: 400
        };
      }

      if (response.status === 401) {
        return {
          success: false,
          modifiedSource: '',
          modifiedIncludes: {},
          changes: [],
          tokensUsed: 0,
          balanceRemaining: 0,
          error: 'Session expired. Please log in again.',
          errorCode: 401
        };
      }

      if (response.status === 402) {
        return {
          success: false,
          modifiedSource: '',
          modifiedIncludes: {},
          changes: [],
          tokensUsed: 0,
          balanceRemaining: errorData.available || errorData.current || 0,
          error: errorData.error || 'insufficient_balance',
          errorCode: 402,
          insufficientBalance: true,
          required: errorData.required || EA_MODIFY_COST,
          available: errorData.available || errorData.current || 0,
          purchaseUrl: errorData.purchaseUrl || errorData.purchase_url || null
        };
      }

      throw new Error(errorData.message || `Modification failed: ${response.status}`);
    }

    const data = await response.json();
    const result = data.data || data;

    return {
      success: true,
      modifiedSource: result.modifiedSource || result.modified_source || '',
      modifiedIncludes: result.modifiedIncludes || result.modified_includes || {},
      changes: result.changes || [],
      tokensUsed: result.tokensUsed || result.tokens_used || 0,
      balanceRemaining: result.balanceRemaining || result.balance_remaining || 0,
      error: null,
      errorCode: null
    };
  } catch (error) {
    clearTimeout(timeoutId);

    // Handle timeout
    if (error.name === 'AbortError') {
      return {
        success: false,
        modifiedSource: '',
        modifiedIncludes: {},
        changes: [],
        tokensUsed: 0,
        balanceRemaining: 0,
        error: 'Modification timed out. Please try again.',
        errorCode: 408
      };
    }

    console.error('[qbridgeEaApi] modifyEA failed:', error);
    return {
      success: false,
      modifiedSource: '',
      modifiedIncludes: {},
      changes: [],
      tokensUsed: 0,
      balanceRemaining: 0,
      error: error.message || 'EA modification failed',
      errorCode: null
    };
  }
};

/**
 * Get user's EA analysis history
 * Endpoint: GET /api/q/ea/history?limit=N
 *
 * @param {string} token - JWT token
 * @param {object} options - Query options
 * @param {number} options.limit - Max records to return (default 10, max 50)
 * @returns {Promise<{
 *   success: boolean,
 *   history: Array<{
 *     id: string,
 *     fileName: string,
 *     operation: string,
 *     tokensUsed: number,
 *     createdAt: string
 *   }>,
 *   error: string|null
 * }>}
 */
export const getEAHistory = async (token, options = {}) => {
  const { limit = 10 } = options;

  try {
    // Backend uses only limit param, no offset (returns most recent first)
    const params = new URLSearchParams({
      limit: Math.min(limit, 50).toString() // Max 50
    });

    const response = await fetch(`${baseURL}/api/q/ea/history?${params}`, {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      }
    });

    if (!response.ok) {
      if (response.status === 401) {
        return { success: false, history: [], error: 'Authentication required' };
      }
      throw new Error(`Failed to get history: ${response.status}`);
    }

    const data = await response.json();
    const result = data.data || data;

    return {
      success: true,
      history: result.history || result || [],
      error: null
    };
  } catch (error) {
    console.warn('[qbridgeEaApi] getEAHistory failed:', error);
    return { success: false, history: [], error: error.message };
  }
};

// ═══════════════════════════════════════════════════════════════════════════
// UTILITY FUNCTIONS
// ═══════════════════════════════════════════════════════════════════════════

/**
 * Get file type configuration
 * @param {string} fileType - File type ('expert', 'indicator', 'script')
 * @returns {object} Type configuration with label, color, fullName
 */
export const getFileTypeConfig = (fileType) => {
  return EA_FILE_TYPES[fileType] || EA_FILE_TYPES.expert;
};

/**
 * Extract base name without extension
 * @param {string} fileName - Full file name
 * @returns {string} Base name
 */
export const getBaseName = (fileName) => {
  return fileName.replace(/\.(mq5|mqh)$/i, '');
};

/**
 * Check if file is a QAI version
 * @param {string} fileName - File name to check
 * @returns {boolean} True if this is a _QAI version
 */
export const isQAIVersion = (fileName) => {
  return /_QAI\.(mq5|mqh)$/i.test(fileName);
};

/**
 * Generate QAI version filename
 * @param {string} originalName - Original file name
 * @returns {string} QAI version filename
 */
export const generateQAIFileName = (originalName) => {
  const baseName = getBaseName(originalName);
  const ext = originalName.match(/\.(mq5|mqh)$/i)?.[0] || '.mq5';
  return `${baseName}_QAI${ext}`;
};

/**
 * Validate file size for EA upload
 * @param {string} content - File content as string
 * @returns {{valid: boolean, size: number, maxSize: number, error: string|null}}
 */
export const validateEAFileSize = (content) => {
  const size = new Blob([content]).size;
  const valid = size <= EA_MAX_FILE_SIZE;

  return {
    valid,
    size,
    maxSize: EA_MAX_FILE_SIZE,
    sizeKB: Math.round(size / 1024 * 10) / 10,
    maxSizeKB: EA_MAX_FILE_SIZE / 1024,
    error: valid ? null : `File too large (${Math.round(size / 1024)}KB). Maximum size is ${EA_MAX_FILE_SIZE / 1024}KB.`
  };
};

/**
 * Format file size for display
 * @param {number} bytes - Size in bytes
 * @returns {string} Formatted size string
 */
export const formatFileSize = (bytes) => {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};
